import { Alignment, Api, DisplayType, LogManager, ModelRow, Navigation, ObjectUtil, StringUtil, UrlUtil } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import {
    ClickEvent, Component, ContextMenuLabelProps, CrudDecorator, DataSourceExecutionEvent, DataSourceMode, ForcedCase, Label,
    Layout, LookupModelSelectionEvent, Snackbar, TableRowDisplayEvent
} from "@mcleod/components";
import { CommonTooltips } from "@mcleod/common";
import { OrderTabView } from "@mcleod/dispatch";
import { StatusAddOrderDialog } from "./StatusAddOrderDialog";
import { ViewEdiStatus } from "./ViewEdiStatus";
import { AutogenLayoutStatusList } from "./autogen/AutogenLayoutStatusList";
import { StatusListToolPanel } from "./StatusListToolPanel";

const log = LogManager.getLogger("lme.datafusion.Statuses");

interface StatusListNavigateProps {
    key?: string;
    newTab?: boolean;
    additionalProps?: {};
    styleProps?: {};
}

const layoutURL = "lme/datafusion/ViewEdiStatus";

export class StatusList extends AutogenLayoutStatusList {
    public static navigateTo(props: StatusListNavigateProps): void {
        Navigation.navigateTo(
            layoutURL + `?mode=${props.key ? DataSourceMode.UPDATE : DataSourceMode.ADD}`
            + (props.key ? `&key=${props.key}` : ""),
            { newTab: props.newTab, ...props.styleProps },
            { additionalProps: props.additionalProps }
        );
    }


    override onLoad() {
        this.setupListeners();
        this.switchSwitchError.allowIndeterminate = true;
        this.switchSwitchError.checked = undefined;
        this.switchSwitchIntercompany.allowIndeterminate = true;
        this.switchSwitchIntercompany.checked = undefined;
        this.switchTransmitted.allowIndeterminate = true;
        this.switchTransmitted.checked = undefined;
        this.citystateCurr.dataSourceModeChanged(DataSourceMode.SEARCH);
        this.citystateCurr.textCity.forcedCase = ForcedCase.UPPER;
        this.citystateShipper.dataSourceModeChanged(DataSourceMode.SEARCH);
        this.citystateShipper.textCity.forcedCase = ForcedCase.UPPER;
        this.citystateCons.dataSourceModeChanged(DataSourceMode.SEARCH);
        this.citystateCons.textCity.forcedCase = ForcedCase.UPPER;
        this.switchSwitchIsResend.allowIndeterminate = true;
        this.switchSwitchIsResend.checked = undefined;
        this.layoutStatusListToolPanel.addMountListener(() => {
            this.tableStatuses.addMountListener(() => this.layoutStatusListToolPanel.initialize(this.tableStatuses))
        })
        const urlProp = UrlUtil.getPropsFromUrl(window.location.search);
        if (urlProp != null && urlProp.id != null)
            this.mainDataSource.search({ order_id: urlProp.id });
    }

    beforeExecution(event: DataSourceExecutionEvent) {
        if (this.switchSwitchError.checked != undefined) {
            const hasErr = this.switchSwitchError.checked ? "Y" : "N";
            this.mainDataSource.searchRow.set("has_error", hasErr);
        }
        if (this.textboxError?.text > "") {
            if (!/^[0-9><]/.test(this.textboxError.text)) {
                Snackbar.showWarningSnackbar("Invalid input for Error Code field.");
                this.mainDataSource.searchRow.set("error", undefined);
            }
        }
        if (ObjectUtil.isObject(event.filter) && Object.keys(event.filter).length == 0) {
            event.filter["empty_search"] = "*";
        }
    }

    tableStatusesOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = event.getTableRow().data as ModelRow;
        tableRow.contextMenuItems = () => StatusListToolPanel.getContextMenuItems(this.layoutStatusListToolPanel, tableRow)
        const labelPartnerDesc = tableRow.findComponentById("labelPartnerDesc") as Label;
        const labelCityState = tableRow.findComponentById("labelCityState") as Label;
        const labelErrorDesc = tableRow.findComponentById("labelErrorDesc") as Label;
        const labelReason = tableRow.findComponentById("labelReason") as Label;
        const labelOrderId = tableRow.findComponentById("labelOrderId") as Label;
        const labelShipmentId = tableRow.findComponentById("labelShipmentId") as Label;
        const labelTransmitted = tableRow.findComponentById("labelTransmitted") as Label;
        const labelBOL = tableRow.findComponentById("labelBOL") as Label;
        this.setTooltipContent(labelPartnerDesc, modelRow);
        this.setTooltipContent(labelCityState, modelRow);
        this.setTooltipContent(labelReason, modelRow);
        if (modelRow.get("order_id")) labelOrderId.addUnderlineWhenMouseOver();

        if (modelRow.get("has_error_out") !== "--") {
            labelErrorDesc.imageName = ImageName.circleErrorSolid;
            labelErrorDesc.color = "error";
            this.setTooltipContent(labelErrorDesc, modelRow);
        } else {
            labelErrorDesc.imageName = null;
            labelErrorDesc.color = "default"
        }
        if (labelShipmentId?.text?.length > 0 || labelBOL?.text?.length > 0) {
            this.setTooltipContent(labelShipmentId, modelRow);
            this.setTooltipContent(labelBOL, modelRow);
        }
        this.setTooltipContent(labelTransmitted, modelRow);
        const direction = modelRow.get("direction");
        const transmitted = modelRow.get("transmitted");

        if (direction === "I") {
            tableRow.allowDelete = true; // Inbound can always be deleted
        } else {
            tableRow.allowDelete = transmitted !== "Yes"; // Outbound only if not transmitted
        }
    }

    setTooltipContent(component: Component, modelRow: ModelRow) {
        if (component.id == null) return;
        const locId = StringUtil.isEmptyString(modelRow.get("curr_loc_id")) ? "--" : modelRow.get("curr_loc_id");

        switch (component.id) {
            case "labelPartnerDesc":
                CommonTooltips.setTooltipContentLayout(component, null, "lme/datafusion/EdiProfileStatusQuickInfo", null,
                    {
                        "direction": modelRow.get("direction"),
                        "partner_id": modelRow.get("partner_id"),
                        "alt_partner_id": modelRow.get("alt_partner_id"),
                        "version": modelRow.get("version"),
                        "batch": modelRow.get("batch")
                    }
                );
                component.tooltipDynamicOptions = { position: Alignment.LEFT };
                return;
            case "labelCityState":
                return CommonTooltips.setTooltipContentTextboxes(component, () => [
                    { caption: "Name", displayType: DisplayType.STRING, text: modelRow.get("curr_loc_name") },
                    { caption: "Location ID", displayType: DisplayType.STRING, text: locId },
                    { caption: "Address", displayType: DisplayType.STRING, text: modelRow.get("curr_address") }
                ]);
            case "labelErrorDesc":
                CommonTooltips.setTooltipContentLayout(component, null, "lme/datafusion/StatusErrorQuickInfo", null,
                    {
                        "parent_id": modelRow.get("id"), "parent_tablename": "edistatus"
                    }
                );
                component.tooltipDynamicOptions = { position: Alignment.LEFT };
                return;
            case "labelReason":
                return CommonTooltips.setTooltipContentTextboxes(component, () => [
                    { caption: "Delay Description", displayType: DisplayType.STRING, text: modelRow.get("delay_desc") }
                ]);
            case "labelShipmentId":
                CommonTooltips.setTooltipContentTextboxes(component, () => [
                    { caption: "Bill of Lading", displayType: DisplayType.STRING, text: modelRow.get("blnum") },
                    { caption: "Shipment ID", displayType: DisplayType.STRING, text: modelRow.get("shipment_id") }
                ]);
                component.tooltipDynamicOptions = { position: Alignment.LEFT };
                return;
            case "labelBOL":
                CommonTooltips.setTooltipContentTextboxes(component, () => [
                    { caption: "Bill of Lading", displayType: DisplayType.STRING, text: modelRow.get("blnum") },
                    { caption: "Shipment ID", displayType: DisplayType.STRING, text: modelRow.get("shipment_id") }
                ]);
                component.tooltipDynamicOptions = { position: Alignment.LEFT };
                return;
            case "labelTransmitted":
                CommonTooltips.setTooltipContentLayout(component, null, "lme/datafusion/TransmissionStatusQuickInfo", null, {
                        "direction": "O",
                        "batch": modelRow.get("batch"),
                        "func_receiver_id": modelRow.get("partner_id"),
                        "receiver_id": modelRow.get("alt_partner_id"),
                        "func_version": modelRow.get("version"),
                        "transaction_type": "S"
                    }
                );
                component.tooltipDynamicOptions = { position: Alignment.LEFT };
                return;
        }
    }

    orderNumberOnClick(event: ClickEvent) {
        if ((event.target as Label).text) {
            OrderTabView.navigateTo({ key: (event.target as Label).text, newTab: true });
        }
    }

    switchOnChange(event) {
        this.textboxError.visible = this.switchSwitchError.checked === true;
        this.textboxErrorDescr.visible = this.switchSwitchError.checked === true;
    }

    setupListeners() {
        this.locationStatusCity.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection() === true) {
                this.textboxCurrLocId.text = event.selection.get("id");
                this.textboxCurrLocName.text = event.selection.get("name");
                this.textboxCurrAddress.text = event.selection.get("address1");
                this.citystateCurr.textCity.text = event.selection.get("city_name");
                this.citystateCurr.textState.text = event.selection.get("state");
                this.citystateCurr.textZip.text = event.selection.get("zip_code");
            }
        });
        this.locationShipper.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection() === true) {
                this.textboxShipLocId.text = event.selection.get("id");
                this.textboxShipLocName.text = event.selection.get("name");
                this.textboxShipAddress.text = event.selection.get("address1");
                this.citystateShipper.textCity.text = event.selection.get("city_name");
                this.citystateShipper.textState.text = event.selection.get("state");
                this.citystateShipper.textZip.text = event.selection.get("zip_code");
            }
        });
        this.locationCons.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection() === true) {
                this.textboxConsLocId.text = event.selection.get("id");
                this.textboxConsLocName.text = event.selection.get("name");
                this.textboxConsAddress.text = event.selection.get("address1");
                this.citystateCons.textCity.text = event.selection.get("city_name");
                this.citystateCons.textState.text = event.selection.get("state");
                this.citystateCons.textZip.text = event.selection.get("zip_code");
            }
        });
    }

    textboxErrorChange() {
        if (this.textboxError.text !== null && this.textboxError.text !== "" && this.textboxError.text !== undefined) {
            this.textboxErrorDescr.enabled = false;
        } else {
            this.textboxErrorDescr.enabled = true;
        }
    }

    textboxErrorDescrChange() {
        if (this.textboxErrorDescr.text !== null && this.textboxErrorDescr.text !== "" && this.textboxErrorDescr.text !== undefined) {
            this.textboxError.enabled = false;
        } else {
            this.textboxError.enabled = true;
        }
    }

    async addButtonOnClick() {
        this.buttonAddRecord.busy = true;
        const dialog = new StatusAddOrderDialog({ okVisible: false, title: "Add New Status" });
        dialog.show().then(async response => {
            if (!StringUtil.isEmptyString(response?.order_id)
                && !StringUtil.isEmptyString(response?.partner_id)
                && !StringUtil.isEmptyString(response?.alt_partner_id)
                && !StringUtil.isEmptyString(response?.version)) {
                Api.post("lme/datafusion/add-order-to-shipment", {
                    order_id: response.order_id,
                    partner_id: response.partner_id,
                    alt_partner_id: response.alt_partner_id,
                    version: response.version,
                    direction: "O"
                }).then(async apiResponse => {
                    const orderId = apiResponse?.data[0]?.order_id;
                    if (!StringUtil.isEmptyString(orderId)) {
                        const statusLayout = Layout.getLayout(this.tableStatuses.detailLayout) as ViewEdiStatus;
                        const urlParam = { mode: "add", key: orderId };
                        const url = "/" + this.tableStatuses.detailLayout + UrlUtil.buildQueryString(urlParam);
                        const statusPanel = new CrudDecorator({
                            layout: statusLayout,
                            mode: DataSourceMode.ADD,
                            pseudoNavigateUrl: url,
                            headerProps: {
                                showClose: true,
                                showSaveAndClose: true,
                            },
                            key: orderId,
                            providedApiData: apiResponse.data[0]
                        }) as any;
                        await statusPanel.slideIn();
                    } else {
                        Snackbar.showWarningSnackbar("Invalid input for Order Id.");
                    }
                    this.buttonAddRecord.busy = false;
                })
            } else {
                this.buttonAddRecord.busy = false;
            }
        });

    }

    public static addNavigationContextMenuItems(itemProps: Partial<ContextMenuLabelProps>[], statusId: string): Partial<ContextMenuLabelProps>[] {
        if (itemProps != null) {
            const navigationItems = StatusList.createNavigationContextMenuItems(statusId);
            if (itemProps.length > 0 && navigationItems.length > 0) {
                const allVisibleItems = itemProps.filter(item => item.visible);
                const lastVisibleItem = allVisibleItems[allVisibleItems.length - 1];
                if (lastVisibleItem) lastVisibleItem.separatorAfter = true;
                itemProps.push(...navigationItems);
            }
        }
        return itemProps;
    }

    public static createNavigationContextMenuItems(statusId: string): Partial<ContextMenuLabelProps>[] {
        if (statusId == null) return [];
        return [
            {
                text: "Open in New Tab",
                onClick: () => StatusList.navigateTo({
                    key: statusId,
                    newTab: true
                })
            },
            {
                text: "Open in New Window",
                onClick: () => StatusList.navigateTo({
                    key: statusId,
                    newTab: true,
                    styleProps: { windowDecorators: false }
                })
            }
        ]
    }

    switchBatchOnChange() {
        this.textboxBeginningBatch.visible = this.switchBatchRange.checked === true;
        this.textboxEndingBatch.visible = this.switchBatchRange.checked === true;
    }
}
